export const BLOG_CATEGORIES = [
  'All',
  'Operations',
  'Customer Experience',
  'Business Systems',
  'Cleaning Industry',
  'Marketing',
  'SEO',
  'Social Media',
  'Growth',
];

// Full posts (with body content) — featured articles.
// Additional 17 topic ideas are listed below as planned articles.
export const BLOG_POSTS = [
  {
    slug: 'signs-your-business-needs-better-systems',
    title: 'Signs your business needs better systems',
    category: 'Business Systems',
    date: 'November 18, 2025',
    read: '6 min read',
    excerpt: 'Most service businesses do not need more help. They need better systems behind the help they already have. Here is what to look for.',
    image: 'https://images.pexels.com/photos/13059862/pexels-photo-13059862.jpeg',
    body: [
      'There is a moment in every growing service business when the founder realises the operating model that got them here will not carry them forward. The work is still good. The team is still capable. But something is wearing down — usually the owner.',
      'The signs are predictable. You are the bottleneck for every quote and decision. The same questions get asked twice. Invoices go out late, follow-ups slip, and the calendar starts to feel like a series of small fires.',
      'These are not people problems. They are systems problems. A well-designed system makes it possible for a capable team to do the right thing consistently — without the owner standing over them.',
      'The fastest fixes are usually the smallest: one inbox of truth, one client follow-up cadence, one place where every team member can see the day. Build those, document them, and let them compound.',
    ],
  },
  {
    slug: 'why-operational-bottlenecks-slow-growth',
    title: 'Why operational bottlenecks slow growth — even when sales are strong',
    category: 'Operations',
    date: 'November 4, 2025',
    read: '5 min read',
    excerpt: 'Strong sales can mask a weak operating model. Eventually the operations catch up — and they decide whether your growth holds or breaks.',
    image: 'https://images.unsplash.com/photo-1719760518176-e124a5bcd025',
    body: [
      'It is possible to grow revenue faster than your business can absorb it. When that happens, the cracks appear in operations — late deliveries, missed follow-ups, frustrated clients, exhausted teams.',
      'Bottlenecks are not always visible. The most common one is the owner: every decision, every escalation, every approval lands on the same desk. The business runs at the speed of one person.',
      'The fix is not heroic. It is structural. Define who owns what. Document the steps. Remove the owner from the workflows that do not need them. Repeat.',
    ],
  },
  {
    slug: 'how-reliable-support-reduces-mental-load',
    title: 'How reliable support reduces mental load for business owners',
    category: 'Operations',
    date: 'October 22, 2025',
    read: '4 min read',
    excerpt: 'The cost of unreliable help is not just the work that does not get done. It is the energy spent worrying whether it will.',
    image: 'https://images.pexels.com/photos/7599590/pexels-photo-7599590.jpeg',
    body: [
      'Many owners do not need more help. They need help they can trust.',
      'The mental load of unreliable support is invisible until you remove it. The constant checking. The mental tabs left open. The relief that does not arrive because you cannot quite let go.',
      'Reliable support is not about doing more. It is about doing the agreed work, consistently, with ownership — and communicating in writing so the owner does not have to hold every detail in their head.',
    ],
  },
  {
    slug: 'five-customer-service-mistakes-service-businesses-make',
    title: '5 customer service mistakes service businesses make',
    category: 'Customer Experience',
    date: 'October 8, 2025',
    read: '6 min read',
    excerpt: 'Most lost customers do not complain. They simply stop calling. Here are the five quiet mistakes that drive them away.',
    image: 'https://images.pexels.com/photos/8360032/pexels-photo-8360032.jpeg',
    body: [
      'Slow first responses. Inconsistent tone across the team. Missed follow-ups. Surprises in the invoice. No closing-the-loop after the work is done.',
      'None of these are catastrophic in isolation. Together, they form the experience that decides whether a client books again, refers you, or quietly moves on.',
      'Customer experience is operational. Fix the systems behind it, and the experience will fix itself.',
    ],
  },
  {
    slug: 'local-seo-basics-for-service-businesses',
    title: 'Local SEO basics for service-based businesses',
    category: 'SEO',
    date: 'September 24, 2025',
    read: '7 min read',
    excerpt: 'A clear, jargon-free guide to local SEO for service businesses that want to show up when nearby customers search.',
    image: 'https://images.pexels.com/photos/30715673/pexels-photo-30715673.jpeg',
    body: [
      'Local SEO is the practical work of being easy to find when someone nearby searches for the services you offer.',
      'It starts with a fully completed Google Business Profile — categories, services, hours, photos, posts, and a steady cadence of reviews. It continues with a website that is fast, mobile-friendly, and structured so each service and location has a dedicated page.',
      'You do not need agency-grade SEO to win locally. You need the basics done well, kept current, and supported by consistent customer care.',
    ],
  },
  {
    slug: 'how-to-build-better-follow-up-processes',
    title: 'How to build better follow-up processes',
    category: 'Business Systems',
    date: 'September 10, 2025',
    read: '5 min read',
    excerpt: 'Follow-up is the work that compounds. Get the process right and your booking rate, repeat business and referrals all improve.',
    image: 'https://images.pexels.com/photos/13059862/pexels-photo-13059862.jpeg',
    body: [
      'Follow-up is operational, not heroic. The teams that follow up consistently do it because they have a system — not because they are remarkably more disciplined than anyone else.',
      'The system has three parts: a single place where every open thread is visible, a clear cadence for first, second and third touches, and templated language that is easy to personalise.',
      'Set it up once. Refine it quarterly. Trust the compounding.',
    ],
  },
];

// Planned blog topics — 20 ideas, with the 6 above as featured articles.
export const BLOG_TOPIC_IDEAS = [
  'Signs your business needs better systems',
  'Why operational bottlenecks slow growth',
  'How reliable support reduces mental load',
  '5 customer service mistakes service businesses make',
  'Local SEO basics for service-based businesses',
  'How to build better follow-up processes',
  'The first five SOPs every service business should document',
  'When to hire a fractional operator vs. a full-time hire',
  'Why your CRM is empty — and how to fix it',
  'A simple weekly operations rhythm for owner-led teams',
  'How to onboard a new client without losing time',
  'What a great review response looks like (with examples)',
  'Hiring for reliability in a service business',
  'How to write a job description that attracts the right people',
  'The cleaning company growth checklist',
  'How to redesign a service website without losing rankings',
  'Three Google Business Profile mistakes that cost you calls',
  'Designing a social media plan for a service business',
  'How to measure whether SEO is actually working',
  'What to do when your team has grown faster than your systems',
];

export const getPostBySlug = (slug) => BLOG_POSTS.find((p) => p.slug === slug);
